// File: resourcepage.js
//
// The Resources page. Shows the SC 2-1-1 community resource directory
// embedded in an iframe, plus quick links to other resource sites
// like Trident United Way.
//
// The two main URLs are constants near the top of ResourcePage()
// below (SC211_URL, TUW_URL). Edit those to point at different
// resource sites.
//
// Sections in this file:
//   - Icons (small SVGs)
//   - Helper components: SoftCard, ActionLink, ResourceLinkCard,
//     EmbedPanel (the iframe wrapper)
//   - ResourcePage (the page itself)

import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "./firebaseConfig";
import { signOut } from "firebase/auth";

import logo from "./assets/512.png";
import { COLORS, THEME, pageBackground } from "./theme";
import LogoutConfirm from "./components/LogoutConfirm";

function IconExternal({ className = "w-5 h-5" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 3h7v7" />
      <path d="M10 14L21 3" />
      <path d="M21 14v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

function IconResources({ className = "w-6 h-6" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 18v-6" />
      <path d="M12 8h.01" />
      <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function IconPhone({ className = "w-5 h-5" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2A19.79 19.79 0 013.08 5.18 2 2 0 015.06 3h3a2 2 0 012 1.72c.12.9.33 1.77.62 2.61a2 2 0 01-.45 2.11L9 10.67a16 16 0 006.33 6.33l1.23-1.23a2 2 0 012.11-.45c.84.29 1.71.5 2.61.62A2 2 0 0122 16.92z" />
    </svg>
  );
}

function SoftCard({ children, className = "", accent }) {
  return (
    <div
      className={`rounded-3xl border backdrop-blur relative overflow-hidden ${className}`}
      style={{
        backgroundColor: THEME.cardBg,
        borderColor: THEME.border,
        boxShadow: THEME.shadow,
      }}
    >
      {accent ? (
        <div
          className="absolute inset-x-0 top-0 h-1"
          style={{ backgroundColor: accent }}
        />
      ) : null}

      {children}
    </div>
  );
}

function ActionLink({ href, children, accent = COLORS.aquaDark }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-extrabold border bg-white hover:-translate-y-0.5 hover:shadow-md active:scale-[0.99] transition"
      style={{
        color: accent,
        borderColor: THEME.border,
        boxShadow: "0 10px 22px rgba(31,56,0,0.08)",
      }}
    >
      <IconExternal />
      {children}
    </a>
  );
}

// A clickable card that links out to a resource website (opens in a
// new tab). Used in the side panel under "More Resources". To add a
// link, find where this is used in the JSX below and copy a usage.
function ResourceLinkCard({ title, description, url, accent }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="text-left rounded-2xl border transition p-5 backdrop-blur relative overflow-hidden hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.99] block"
      style={{
        backgroundColor: THEME.cardBg,
        borderColor: THEME.border,
        boxShadow: "0 14px 28px rgba(31,56,0,0.1)",
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ backgroundColor: accent }}
      />

      <div className="flex items-start gap-3">
        <div
          className="rounded-xl p-2 border bg-white"
          style={{
            color: accent,
            borderColor: THEME.border,
          }}
        >
          <IconExternal />
        </div>

        <div className="min-w-0 flex-1">
          <div
            className="text-base font-extrabold"
            style={{ color: THEME.text }}
          >
            {title}
          </div>

          <div
            className="text-sm mt-1"
            style={{ color: "rgba(31,56,0,0.66)" }}
          >
            {description}
          </div>
        </div>
      </div>
    </a>
  );
}

// Shows the resource directory in an embedded iframe. If the iframe
// doesn't load within ~3.5 seconds, a hint appears telling the user
// to use the "Open" button instead. (Some sites block embedding.)
function EmbedPanel({ title, url }) {
  const [timedOut, setTimedOut] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setTimedOut(false);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      setTimedOut(true);
    }, 3500);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [url]);

  const handleLoad = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setTimedOut(false);
  };

  return (
    <SoftCard className="p-4 sm:p-6" accent={COLORS.aquaBase}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <div
            className="text-sm font-extrabold"
            style={{ color: COLORS.aquaDark }}
          >
            Embedded Directory
          </div>

          <h2
            className="text-2xl sm:text-3xl font-extrabold mt-1"
            style={{ color: THEME.text }}
          >
            {title}
          </h2>

          <p
            className="text-sm mt-2 max-w-2xl"
            style={{ color: "rgba(31,56,0,0.66)" }}
          >
            Search for food, housing, healthcare, family support, and other local
            services directly from the directory below.
          </p>
        </div>

        <ActionLink href={url} accent={COLORS.aquaDark}>
          Open full directory
        </ActionLink>
      </div>

      {timedOut ? (
        <div
          className="mt-5 rounded-2xl border p-4 text-sm"
          style={{
            backgroundColor: COLORS.yellowLight,
            borderColor: "rgba(61,51,0,0.18)",
            color: COLORS.brown,
          }}
        >
          If this embed stays blank, use the open button above to view the
          directory in a new tab.
        </div>
      ) : null}

      <div
        className="mt-5 rounded-2xl border overflow-hidden bg-white"
        style={{
          borderColor: THEME.border,
          boxShadow: "0 12px 26px rgba(31,56,0,0.08)",
        }}
      >
        <iframe
          title={title}
          src={url}
          onLoad={handleLoad}
          className="w-full h-[55vh] sm:h-[58vh] lg:h-[62vh]"
          style={{ border: "0px" }}
          loading="lazy"
          allow="clipboard-read; clipboard-write; fullscreen"
        />
      </div>
    </SoftCard>
  );
}

function ResourcePage() {
  const navigate = useNavigate();

  // Drives the logout confirmation popup.
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // Signs the user out and sends them back to the login page.
  const handleLogout = useCallback(async () => {
    try {
      setLoggingOut(true);
      await signOut(auth);
      localStorage.removeItem("userEmail");
      navigate("/", { replace: true });
    } catch (e) {
      console.error("Logout failed:", e);
      alert("Logout failed. Please try again.");
      setLoggingOut(false);
      setLogoutConfirmOpen(false);
    }
  }, [navigate]);

  // External resource site URLs. Edit these to point at different
  // directories, or add more in the side panel JSX below.
  const SC211_URL = "https://resources.sc211.org/#/";
  const TUW_URL = "https://www.tuw.org/find-resources";

  const sc211EmbedUrl = useMemo(() => SC211_URL, []);

  return (
    <div className="flex-1 relative" style={pageBackground}>
      {/* --- Page header: logo on left, Home + Logout on right --- */}
      <header
        className="sticky top-0 z-30 backdrop-blur border-b shadow-[0_6px_18px_rgba(31,56,0,0.16)]"
        style={{
          backgroundColor: THEME.headerBg,
          borderColor: "rgba(231,247,210,0.28)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-3 rounded-xl px-2 py-1 hover:bg-white/10 transition"
            aria-label="Go to home"
            type="button"
          >
            <img
              src={logo}
              alt="Logo"
              className="w-13 h-12 object-contain"
            />

            <div className="leading-tight text-left">
              <div className="text-sm font-extrabold text-white">
                H.O.P.E Navigator
              </div>
              <div
                className="text-[12px] font-semibold"
                style={{ color: COLORS.yellowLight }}
              >
                Resources
              </div>
            </div>
          </button>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate("/home")}
              className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
              type="button"
            >
              Home
            </button>

            <button
              onClick={() => setLogoutConfirmOpen(true)}
              className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
              type="button"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* --- Main two-column layout. Left = page intro + embedded
              SC 2-1-1 directory. Right (sidebar) = quick links to
              external resource sites. --- */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          <section>
            <SoftCard className="p-5 sm:p-6 lg:p-8" accent={COLORS.yellowBase}>
              <div
                className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-extrabold"
                style={{
                  color: COLORS.brown,
                }}
              >
                <IconResources className="w-4 h-4" />
                Resources
              </div>

              <h1
                className="text-3xl sm:text-4xl font-extrabold mt-4"
                style={{ color: THEME.text }}
              >
                Community Resource Finder
              </h1>

              <p
                className="mt-2 max-w-2xl"
                style={{ color: "rgba(31,56,0,0.66)" }}
              >
                Browse local support options, search the SC 2-1-1 directory, or
                open trusted resource directories in a new tab.
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ResourceLinkCard
                  title="SC 2-1-1"
                  description="Search a statewide directory of local services and support."
                  url={SC211_URL}
                  accent={COLORS.aquaBase}
                />

                <ResourceLinkCard
                  title="Trident United Way"
                  description="Find regional community resources through Trident United Way."
                  url={TUW_URL}
                  accent={COLORS.greenDark}
                />
              </div>
            </SoftCard>

            <div className="mt-6">
              <EmbedPanel title="SC 2-1-1" url={sc211EmbedUrl} />
            </div>
          </section>

          <aside className="lg:block">
            <div className="lg:sticky lg:top-[72px] space-y-4">
              <SoftCard className="p-6" accent={COLORS.greenDark}>
                <div
                  className="text-sm font-extrabold"
                  style={{ color: THEME.text }}
                >
                  Need help now?
                </div>

                <div
                  className="mt-4 rounded-2xl border p-4"
                  style={{
                    backgroundColor: COLORS.greenLight,
                    borderColor: THEME.border,
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="rounded-xl p-2 border bg-white"
                      style={{
                        color: COLORS.greenDark,
                        borderColor: THEME.border,
                      }}
                    >
                      <IconPhone />
                    </div>

                    <div>
                      <div
                        className="text-sm font-extrabold"
                        style={{ color: THEME.text }}
                      >
                        Emergency support
                      </div>

                      <p
                        className="text-sm mt-1 leading-6"
                        style={{ color: "rgba(31,56,0,0.68)" }}
                      >
                        In an emergency, call{" "}
                        <span className="font-extrabold">911</span>. For crisis
                        support in the U.S., call or text{" "}
                        <span className="font-extrabold">988</span>.
                      </p>
                    </div>
                  </div>
                </div>
              </SoftCard>

              <SoftCard className="p-6" accent={COLORS.aquaBase}>
                <div
                  className="text-sm font-extrabold"
                  style={{ color: THEME.text }}
                >
                  Quick Links
                </div>

                <div className="mt-4 space-y-3">
                  <ActionLink href={SC211_URL} accent={COLORS.aquaDark}>
                    SC 2-1-1
                  </ActionLink>

                  <ActionLink href={TUW_URL} accent={COLORS.greenDark}>
                    Trident United Way
                  </ActionLink>
                </div>
              </SoftCard>
            </div>
          </aside>
        </div>
      </main>

      <LogoutConfirm
        open={logoutConfirmOpen}
        busy={loggingOut}
        onCancel={() => setLogoutConfirmOpen(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}

export default ResourcePage;